import { CONTENT_TYPE } from 'generated/types/contentful'
import { getEnv } from 'lib/envWrapper'
import { Sys } from 'contentful'

export enum ContentfulErrors {
	NotFound = 'NotFound',
	TooFewResources = 'TooFewResources',
	Unknown = 'Unknown'
}

export const defaultSysObject: SystemFields = { createdAt: new Date(), updatedAt: new Date() }

export class RequestRedirect {
	public redirect = true
	constructor(public path: string) {}
}

export type ContentfulConstraint = { [x: string]: string | boolean }

export interface ContentfulQuery {
	content_type: CONTENT_TYPE
	include?: number
	order?: string
	limit?: number
	skip?: number
}

interface SystemFields {
	updatedAt: Date
	createdAt: Date
}
export interface ContentfulEntity {
	sys: SystemFields
	id: string
}

export const parseSystemFields = (sys: Sys): SystemFields => {
	return {
		createdAt: new Date(sys.createdAt),
		updatedAt: new Date(sys.updatedAt)
	}
}
export const mergeConstraints = (constraints: ContentfulConstraint[]) => {
	const x = {}
	constraints.forEach(c => Object.assign(x, c))
	return x
}

/**
 * https://stackoverflow.com/a/12646864
 * @param array
 */
export const shuffleArray = <T>(array: T[]) => {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1))
		;[array[i], array[j]] = [array[j], array[i]]
	}
	return array
}

type referenceFn<T> = (id: string) => Promise<T[]>

const defaultContentfulSorter = (entity: ContentfulEntity) => entity.sys.createdAt.valueOf()

/**
 * Given a list of keys and a fetcher, retrieves all values for each key and returns a merged array.
 * Useful for when contentful doesnt support querying multiple referenced values.
 * Sorter defaults to created date
 * @param list
 * @param fetcher
 * @param sortWeight
 */
export const referencesMultiple = async <T extends ContentfulEntity>(
	list: string[],
	fetcher: referenceFn<T>,
	sortWeight: (x: T) => number = defaultContentfulSorter
): Promise<T[]> => {
	const promises = list.map(id => fetcher(id))
	const result = await Promise.all(promises)
	const merged = result.reduce((accum, val) => {
		const filtered = val.filter(x => x.id && accum.map(i => i.id).indexOf(x.id) === -1)
		return [...accum, ...filtered]
	}, [])
	return merged.sort((a, b) => sortWeight(b) - sortWeight(a))
}

/**
 * Remove undefined (or any falsy value) from an array
 * @param array
 */
export const removeFalsy = <T>(array: Array<T | undefined>): Array<T> => {
	return array.reduce((accum, value) => {
		return value ? [...accum, value] : accum
	}, [] as T[])
}

/**
 * Ensures the URL is of format that a browser can resolve
 * @param url
 */
export const ensureAbsoluteUrl = (url: string) => {
	if (url.indexOf('https://') === 0) return url
	if (url.indexOf('//') === 0) return `https:${url}`
	if (url.split('/')[0]?.includes('.')) return `https://${url}`
	else return `${getEnv().root_domain}${url}`
}
